
/**
 * Tabbed Content Controller Class.
 */
class TabbedContent {

	static defaults = {
		classNames: {
			'activeClass': 'is-active',
			'navScroll': 'mdl_tabbedcontent__nav',
			'navItemEl': 'mdl_tabbedcontent__nav-item',
			'contentEl': 'mdl_tabbedcontent__item'
		}
	}

	constructor( containerEl, options = {} ) {

		// Containing Element and options.
		this.containerEl = containerEl;
		this.options = Object.assign( {}, TabbedContent.defaults, options );

		// Display Elements.
		this.navItemEls = Array.from( containerEl.getElementsByClassName( this.options.classNames.navItemEl ) );
		this.contentEls = Array.from( containerEl.getElementsByClassName( this.options.classNames.contentEl ) );

		// Custom scroller.
		if ( this.options.classNames.navScroll ) {
			this.navScrollEl = containerEl.querySelector( '.' + this.options.classNames.navScroll );
		}

		// Add Event Listeners.
		this.navItemEls.forEach( ( navItemEl ) => {
			navItemEl.addEventListener( 'click', () => this.openTab( navItemEl.dataset.tabId ) )
			navItemEl.addEventListener( 'select-tab.sl', () => this.openTab( navItemEl.dataset.tabId ) )
		} );
		window.addEventListener( 'resize', () => this.scrollToTab( this.getActiveTab() ) );

		// Scroll to initially active tab.
		this.scrollToTab( this.getActiveTab() );
	}

	isMultiSelectable = () => {
		return String( this.containerEl.ariaMultiSelectable ) === 'true';
	}

	findTabAndContent = ( tabId ) => {
		if ( tabId instanceof HTMLElement ) {
			tabId = tabId.dataset.tabId;
		}

		const navItemEl = this.navItemEls.find( ( navItemEl ) => navItemEl.dataset.tabId === tabId );
		const contentEl = this.contentEls.find( ( contentEl ) => contentEl.dataset.tabId === tabId );

		if ( ! navItemEl || ! contentEl ) {
			throw "Tabbed content element with [data-tab-id='" + tabId + "'] not found";
		}

		return [ navItemEl, contentEl ];
	}

	getActiveTab = () => {
		return this.navItemEls.find( ( navItemEl ) => this.#isActiveEl( navItemEl ) );
	}

	openTab = ( tabId ) => {

		const [foundNavItemEl, foundContentEl] = this.findTabAndContent( tabId );

		if ( this.isMultiSelectable() ) {
			// Only toggle the found item open / close.
			this.#setNavItem( foundNavItemEl, ! this.#isActiveEl( foundNavItemEl ) );
			this.#setContent( foundContentEl, ! this.#isActiveEl( foundContentEl ) );
		} else {
			// Toggle active class for all elements and set aria attribute.
			this.navItemEls.forEach( ( navItemEl ) => {
				const isActive = navItemEl === foundNavItemEl;
				this.#setNavItem( navItemEl, isActive );
			} );
			this.contentEls.forEach( ( contentEl ) => {
				const isActive = contentEl === foundContentEl;
				this.#setContent( contentEl, isActive );
			} );
		}

		// Trigger Custom Lazyload so the new contents get loaded if necessary.
		this.scrollToTab( foundNavItemEl );
		foundContentEl.dispatchEvent( new CustomEvent( 'lazyload.tao', { bubbles: true } ) );
	}

	#isActiveEl = ( navItemEl ) => {
		return navItemEl.classList.contains( this.options.classNames.activeClass );
	}

	#setNavItem = ( navItemEl, isActive = true ) => {
		navItemEl.classList.toggle( this.options.classNames.activeClass, isActive );
		navItemEl.ariaSelected = isActive ? 'true' : 'false';
		navItemEl.ariaExpanded = isActive ? 'true' : 'false';
	}

	#setContent = ( contentEl, isActive = true )  => {
		contentEl.classList.toggle( this.options.classNames.activeClass, isActive );
		contentEl.ariaHidden = isActive ? 'false' : 'true';
	}

	scrollToTab = ( navItemEl ) => {

		// Get Scroll Container.
		const navScrollEl = this.navScrollEl;

		// No Element found.
		if ( ! navItemEl || ! navScrollEl ) {
			return;
		}

		// Nothing to scroll.
		if ( navScrollEl.scrollWidth < navScrollEl.clientWidth ) {
			return;
		}

		// Active element is already completely in view.
		const inBoundsLeft  = navItemEl.offsetLeft >= navScrollEl.scrollLeft;
		const inBoundsRight = navItemEl.offsetLeft + navItemEl.offsetWidth <= navScrollEl.scrollLeft + navScrollEl.clientWidth;
		if ( inBoundsLeft && inBoundsRight ) {
			return;
		}

		// Scroll to the active tab - Animated via scroll-behavior: smooth;
		navScrollEl.scrollLeft = navItemEl.offsetLeft;
	}
}

const initTabbedContent = function( scopeEl = document ) {
	const tabbedContentEls = scopeEl.querySelectorAll( '.mdl_tabbedcontent' );
	tabbedContentEls.forEach( containerEl => {
		containerEl.SL_Tabbed_Content ??= new TabbedContent( containerEl )
	} );
}

// This method is not called yet.
const initPDPTabNavigation = ( scopeEl = document ) => {
	const tabNavigationEls = Array.from( scopeEl.getElementsByClassName( 'en_tab__container' ) );
	tabNavigationEls.forEach( containerEl => {
		containerEl.SL_Tabbed_Content ??= new TabbedContent( containerEl, {
			classNames: {
				'activeClass': 'en_is_active',
				'navScroll': null,
				'navItemEl': 'en_tab__nav_item',
				'contentEl': 'en_tab__item'
			}
		} );
	} );
}

export {
	TabbedContent as default,
	initTabbedContent,
	initPDPTabNavigation
};
